import {NavIcon,type NavIconName} from './components/NavIcon';
import {workspaceFetch} from './api';
import type {CompanySummaryBoard} from '../server/crm/pilot-types';
import './operations.css';
import type {Session} from '@supabase/supabase-js';
import {lazy, Suspense, useEffect, useMemo, useState} from 'react';

import {rememberAuthReturn, restorePendingAuthorization} from './auth-return';
import {Button} from './design-system/Button';
import {Spinner} from './components/LoadingState';
import {SupportInboxCache} from './support-inbox-cache';
import {HomePage} from './pages/HomePage';
import type {EmployeeProfile, PortalProps} from './types';

const WebsiteGrowthPage = lazy(() => import('./pages/WebsiteGrowthPage').then(module => ({default: module.WebsiteGrowthPage})));
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage').then(module => ({default: module.KnowledgeBasePage})));
const CrmPage = lazy(() => import('./pages/CrmPage').then(module => ({default: module.CrmPage})));
const ShipPage = lazy(() => import('./pages/ShipPage').then(module => ({default: module.ShipPage})));
const LogReturnPage = lazy(() => import('./pages/LogReturnPage').then(module => ({default: module.LogReturnPage})));
const AssistantPage = lazy(() => import('./pages/AssistantPage').then(module => ({default: module.AssistantPage})));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(module => ({default: module.DashboardPage})));
const CustomerSupportPage = lazy(() => import('./pages/CustomerSupportPage').then(module => ({default: module.CustomerSupportPage})));
const NewInvoicePage = lazy(() => import('./pages/NewInvoicePage').then(module => ({default: module.NewInvoicePage})));
const GiveawaysPage = lazy(() => import('./pages/GiveawaysPage').then(module => ({default: module.GiveawaysPage})));
const OrdersPage = lazy(() => import('./pages/OrdersPage').then(module => ({default: module.OrdersPage})));
const OAuthConsentPage = lazy(() => import('./pages/OAuthConsentPage').then(module => ({default: module.OAuthConsentPage})));
const ReturnCheckInPage = lazy(() => import('./pages/ReturnCheckInPage').then(module => ({default: module.ReturnCheckInPage})));
const ReturnsPage = lazy(() => import('./pages/ReturnsPage').then(module => ({default: module.ReturnsPage})));
const TeamPage = lazy(() => import('./pages/TeamPage').then(module => ({default: module.TeamPage})));

type Page = 'website-growth' | 'knowledge-base' | 'crm' | 'ship' | 'log-return' | 'find-anything' | 'new-invoice' | 'support' | 'home' | 'dashboard' | 'orders' | 'returns' | 'return-check-in' | 'giveaways' | 'dev-agent' | 'hiring' | 'assistant' | 'oauth-consent' | 'team';

const PAGE_PATHS: Record<Page, string> = {
  home: '/',
  'website-growth': '/website-growth',
  'knowledge-base':'/knowledge-base',
  crm: '/crm',
  ship: '/ship',
  'log-return': '/returns/log',
  'find-anything': '/find-anything',
  support: '/support',
  'new-invoice': '/new-invoice',
  dashboard: '/dashboard',
  orders: '/orders',
  returns: '/returns',
  'return-check-in': '/returns/check-in',
  giveaways: '/giveaways',
  'dev-agent': '/dev-agent',
  hiring: '/hiring',
  assistant: '/assistant',
  'oauth-consent': '/oauth/consent',
  team: '/team',
};

export function App({supabase}: PortalProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [authenticatedProfile, setProfile] = useState<EmployeeProfile | null>(null);
  const userId=session?.user.id;
  const profile=authenticatedProfile?.user_id===userId?authenticatedProfile:null;
  const supportInboxCache = useMemo(() => new SupportInboxCache(), [userId, profile?.role]);
  const crmSessionCache = useMemo(() => new Map<string,CompanySummaryBoard>(), [userId,profile?.role]);
  const [crmAccess,setCrmAccess]=useState<{userId:string;allowed:boolean;error?:string}|null>(null);
  const [accessRetry,setAccessRetry]=useState(0);
  const crmAllowed=crmAccess?.userId===userId && crmAccess?.allowed===true;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [path, setPath] = useState(() => window.location.pathname);
  const [crmNavigation, setCrmNavigation] = useState(0);
  const [page, setPage] = useState<Page>(() => pageFromPath(window.location.pathname));
  useEffect(() => {document.documentElement.classList.remove('dark'); document.documentElement.style.colorScheme = 'light';}, []);

  useEffect(() => {
    let active = true;
    const applyPendingReturn = (hasSession: boolean) => {
      const restored = restorePendingAuthorization(hasSession);
      if (!restored) return;
      setPath(restored);
      setPage(pageFromPath(restored));
    };
    void supabase.auth.getSession().then(({data, error: sessionError}) => {
      if (!active) return;
      if (sessionError) setError(sessionError.message);
      setSession(data.session);
      applyPendingReturn(Boolean(data.session));
      setLoading(false);
    });
    const {data} = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      // The callback often resolves after getSession() above has already run.
      applyPendingReturn(Boolean(nextSession));
      setLoading(false);
    });
    const onPopState = () => {
      setPath(window.location.pathname);
      setPage(pageFromPath(window.location.pathname));
    };
    window.addEventListener('popstate', onPopState);
    return () => {
      active = false;
      data.subscription.unsubscribe();
      window.removeEventListener('popstate', onPopState);
    };
  }, [supabase]);

  useEffect(() => {
    setProfile(null); setCrmAccess(null); setError(null);
    if (!userId) return;
    const controller = new AbortController();
    void workspaceFetch('/api/workspace/access', {signal: AbortSignal.any([controller.signal, AbortSignal.timeout(15000)])}).then(async response => {
      const body = await response.json() as {profile?: EmployeeProfile; crmAllowed?: boolean; error?: string};
      if (!response.ok || body.profile?.user_id !== userId || typeof body.crmAllowed !== 'boolean') throw new Error(body.error || 'Could not verify workspace access.');
      if (!controller.signal.aborted) {setProfile(body.profile); setCrmAccess({userId, allowed: body.crmAllowed});}
    }).catch(error => {if (!controller.signal.aborted) setCrmAccess({userId, allowed: false, error: error.message});});
    return () => controller.abort();
  }, [userId, accessRetry]);

  const navigatePath = (nextPath: string) => {
    window.history.pushState({}, '', nextPath);
    setPath(nextPath);
    setPage(pageFromPath(nextPath));
  };
  const navigate = (next: Page) => navigatePath(PAGE_PATHS[next]);

  const authActions = useMemo(
    () => ({
      signIn: async () => {
        setError(null);
        rememberAuthReturn();
        const {error: signInError} = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: `${window.location.origin}/auth/callback`,
            queryParams: {hd: 'mentraglass.com', prompt: 'select_account'},
          },
        });
        if (signInError) setError(signInError.message);
      },
      signOut: async () => {
        await supabase.auth.signOut({scope: 'global'});
        navigate('home');
      },
    }),
    [supabase],
  );

  if (loading) return <CenteredState title="Opening Mentra Teammate…" />;

  if (!session) {
    return (
      <main className="centered-state">
        <section className="auth-card">
          <div className="brand-mark"><img src="/mentra-mark.svg" alt="Mentra" /></div>
          <p className="eyebrow">Internal tools</p>
          <h1>Mentra Teammate</h1>
          <p>Returns, operations, company metrics, and engineering activity in one place.</p>
          {error && <div className="alert error">{error}</div>}
          <Button onClick={() => void authActions.signIn()}>
            Continue with Google
          </Button>
          <small>Use your @mentraglass.com Workspace account.</small>
        </section>
      </main>
    );
  }

  if(!crmAccess||crmAccess.userId!==userId)return <CenteredState title="Opening Mentra Teammate…" />;
  if(crmAccess.error)return <main className="centered-state"><section className="auth-card"><div className="alert error" role="alert">{crmAccess.error}</div><Button onClick={()=>{setCrmAccess(null);setAccessRetry(value=>value+1);}}>Retry</Button></section></main>;
  if (!profile) return <CenteredState title="Preparing your teammate profile…" />;

  return (
    <div className={page === 'support' ? 'app-shell support-shell' : 'app-shell support-shell teammate-shell'}>
      <aside className="sidebar">
        <button className="wordmark" onClick={() => navigate('home')}>
          <span className="brand-mark small"><img src="/mentra-mark.svg" alt="" /></span>
          <span>Mentra Teammate</span>
        </button>
        <nav>
          <NavButton icon="home" active={page === 'home'} onClick={() => navigate('home')}>Home</NavButton>
          <NavButton icon="assistant" active={page === 'assistant' || page === 'find-anything'} onClick={() => navigate('assistant')}>Assistant</NavButton>
          {crmAllowed && <NavButton icon="crm" active={page === 'crm'} onClick={() => {setCrmNavigation(value => value + 1); navigate('crm');}}>CRM</NavButton>}
          <NavButton icon="support" active={page === 'support'} onClick={() => navigate('support')}>Customer Support</NavButton>
          <NavButton icon="knowledge" active={page === 'knowledge-base'} onClick={() => navigate('knowledge-base')}>Knowledge Base</NavButton>
          {profile.role !== 'employee' && <NavButton icon="ship" active={page === 'ship'} onClick={() => navigate('ship')}>Ship</NavButton>}
          {profile.role !== 'employee' && <NavButton icon="invoice" active={page === 'new-invoice'} onClick={() => navigate('new-invoice')}>New invoice</NavButton>}
          <NavButton icon="growth" active={page === 'website-growth'} onClick={() => navigate('website-growth')}>Website growth</NavButton>
          <NavButton icon="dashboard" active={page === 'dashboard'} onClick={() => navigate('dashboard')}>Dashboard</NavButton>
          {profile.role !== 'employee' && <NavButton icon="orders" active={page === 'orders'} onClick={() => navigate('orders')}>Open orders</NavButton>}
          <NavButton icon="returns" active={page === 'returns' || page === 'return-check-in' || page === 'log-return'} onClick={() => navigate('returns')}>Returns</NavButton>
          {profile.role !== 'employee' && <NavButton icon="giveaways" active={page === 'giveaways'} onClick={() => navigate('giveaways')}>Giveaways</NavButton>}
          {profile.role === 'admin' && (
            <NavButton icon="team" active={page === 'team'} onClick={() => navigate('team')}>Team access</NavButton>
          )}
        </nav>
        <div className="sidebar-profile">
          <div>
            <strong>{profile.display_name ?? profile.email}</strong>
            <span>{profile.role === 'employee' ? 'Teammate' : profile.role}</span>
          </div>
          <button className="text-button" onClick={() => void authActions.signOut()}>Sign out</button>
        </div>
      </aside>
      <main className="content">
        <Suspense fallback={<Spinner label="Opening page…" />}>
        {page === 'crm' && (crmAllowed ? <CrmPage key={crmNavigation} sessionCache={crmSessionCache} /> : <p>Leadership access is required for CRM and company email.</p>)}
        {page === 'knowledge-base' && <KnowledgeBasePage />}
        {page === 'support' && <CustomerSupportPage inboxCache={supportInboxCache} canInvoice={profile.role !== 'employee'} canImport={profile.role === 'admin'} key={profile.user_id} person={{id: profile.user_id, name: profile.display_name || profile.email, email: profile.email}} />}
        {page === 'ship' && session && (profile.role !== 'employee' ? <ShipPage session={session} /> : <p>Operations access is required for shipping.</p>)}
        {page === 'log-return' && <LogReturnPage supabase={supabase} profile={profile} onBack={() => navigate('returns')} />}
        {page === 'find-anything' && session && <AssistantPage session={session} profile={profile} />}
        {page === 'new-invoice' && (profile.role !== 'employee' ? <NewInvoicePage /> : <p>Operations access is required for invoices.</p>)}
        {page === 'home' && <HomePage navigate={navigate} profile={profile} canCrm={crmAllowed} />}
        {page === 'website-growth' && <WebsiteGrowthPage />}
        {page === 'dashboard' && session && <DashboardPage session={session} />}
        {page === 'orders' && (profile.role !== 'employee' ? <OrdersPage supabase={supabase} profile={profile} path={path} onNavigate={navigatePath} /> : <HomePage navigate={navigate} profile={profile} canCrm={crmAllowed} />)}
        {page === 'returns' && <ReturnsPage profile={profile} supabase={supabase} onCheckIn={() => navigate('return-check-in')} onLogReturn={() => navigate('log-return')} />}
        {page === 'return-check-in' && session && <ReturnCheckInPage session={session} profile={profile} supabase={supabase} onBackToReturns={() => navigate('returns')} />}
        {page === 'giveaways' && (profile.role !== 'employee' ? <GiveawaysPage supabase={supabase} profile={profile} path={path} onNavigate={navigatePath} /> : <HomePage navigate={navigate} profile={profile} canCrm={crmAllowed} />)}
        {page === 'dev-agent' && <HomePage navigate={navigate} profile={profile} canCrm={crmAllowed} />}
        {page === 'hiring' && <HomePage navigate={navigate} profile={profile} canCrm={crmAllowed} />}
        {page === 'assistant' && session && <AssistantPage session={session} profile={profile} />}
        {page === 'oauth-consent' && <OAuthConsentPage supabase={supabase} profile={profile} />}
        {page === 'team' && profile.role === 'admin' && <TeamPage supabase={supabase} currentUserId={profile.user_id} />}
        </Suspense>
      </main>
    </div>
  );
}

function NavButton({active, children, onClick,icon}: {icon:NavIconName;active: boolean; children: React.ReactNode; onClick: () => void}) {
  return <button className={active ? 'nav-button active' : 'nav-button'} onClick={onClick} aria-current={active?'page':undefined}><NavIcon name={icon}/><span className="nav-label">{children}</span></button>;
}

function CenteredState({title}: {title: string}) {
  return <main className="centered-state"><section className="auth-card loading-auth-card"><div className="brand-mark"><img src="/mentra-mark.svg" alt="Mentra" /></div><Spinner label={title} /></section></main>;
}

function pageFromPath(path: string): Page {
  if (path === '/website-growth') return 'website-growth';
  if (path === '/knowledge-base' || path.startsWith('/knowledge-base/')) return 'knowledge-base';
  if (path === '/crm' || path.startsWith('/crm/')) return 'crm';
  if (path.startsWith('/ship')) return 'ship';
  if (path.startsWith('/returns/log')) return 'log-return';
  if (path.startsWith('/find-anything')) return 'find-anything';
  if (path.startsWith('/new-invoice')) return 'new-invoice';
  if (path.startsWith('/support')) return 'support';
  if (path.startsWith('/dashboard')) return 'dashboard';
  if (path.startsWith('/orders') || path.startsWith('/fulfillment')) return 'orders';
  if (path.startsWith('/returns/check-in')) return 'return-check-in';
  if (path.startsWith('/returns')) return 'returns';
  if (path.startsWith('/giveaways')) return 'giveaways';
  if (path.startsWith('/dev-agent')) return 'dev-agent';
  if (path.startsWith('/hiring')) return 'hiring';
  if (path.startsWith('/assistant') || path.startsWith('/mcp')) return 'assistant';
  if (path.startsWith('/oauth/consent')) return 'oauth-consent';
  if (path.startsWith('/team')) return 'team';
  return 'home';
}
